/**
 * Overlay reducer for managing stacked modals and drawers.
 *
 * Uses React useReducer (not Redux) so that Component references can be stored
 * directly in state without serialisation concerns.
 *
 * Two-phase close:
 *  1. START_CLOSE_* → sets `closing: true` (triggers exit animation)
 *  2. REMOVE_*      → filters the entry from the array (after animation ends)
 */

export const OVERLAY_ACTIONS = {
  OPEN_MODAL: "OPEN_MODAL",
  START_CLOSE_MODAL: "START_CLOSE_MODAL",
  REMOVE_MODAL: "REMOVE_MODAL",
  CLOSE_ALL_MODALS: "CLOSE_ALL_MODALS",

  OPEN_DRAWER: "OPEN_DRAWER",
  START_CLOSE_DRAWER: "START_CLOSE_DRAWER",
  REMOVE_DRAWER: "REMOVE_DRAWER",
  CLOSE_ALL_DRAWERS: "CLOSE_ALL_DRAWERS",

  CLOSE_ALL: "CLOSE_ALL",
};

export const initialOverlayState = {
  modals: [],
  drawers: [],
  nextSeq: 0,
};

export function overlayReducer(state, action) {
  switch (action.type) {
    // ── Modals ──────────────────────────────────────────────────────
    case OVERLAY_ACTIONS.OPEN_MODAL: {
      const { key, Component, props, options } = action.payload;
      const exists = state.modals.find(m => m.key === key);

      if (exists) {
        return {
          ...state,
          modals: state.modals.map(m => (m.key === key ? { ...m, Component, props, options } : m)),
        };
      }

      return {
        ...state,
        modals: [
          ...state.modals,
          { key, Component, props: props || {}, options: options || {}, seq: state.nextSeq, closing: false },
        ],
        nextSeq: state.nextSeq + 1,
      };
    }

    case OVERLAY_ACTIONS.START_CLOSE_MODAL: {
      const { key } = action.payload;
      return {
        ...state,
        modals: state.modals.map(m => (m.key === key ? { ...m, closing: true } : m)),
      };
    }

    case OVERLAY_ACTIONS.REMOVE_MODAL: {
      const { key } = action.payload;
      return {
        ...state,
        modals: state.modals.filter(m => m.key !== key),
      };
    }

    case OVERLAY_ACTIONS.CLOSE_ALL_MODALS:
      return { ...state, modals: [] };

    // ── Drawers ─────────────────────────────────────────────────────
    case OVERLAY_ACTIONS.OPEN_DRAWER: {
      const { key, Component, props, options } = action.payload;
      const exists = state.drawers.find(d => d.key === key);

      if (exists) {
        return {
          ...state,
          drawers: state.drawers.map(d => (d.key === key ? { ...d, Component, props, options } : d)),
        };
      }

      return {
        ...state,
        drawers: [
          ...state.drawers,
          { key, Component, props: props || {}, options: options || {}, seq: state.nextSeq, closing: false },
        ],
        nextSeq: state.nextSeq + 1,
      };
    }

    case OVERLAY_ACTIONS.START_CLOSE_DRAWER: {
      const { key } = action.payload;
      return {
        ...state,
        drawers: state.drawers.map(d => (d.key === key ? { ...d, closing: true } : d)),
      };
    }

    case OVERLAY_ACTIONS.REMOVE_DRAWER: {
      const { key } = action.payload;
      return {
        ...state,
        drawers: state.drawers.filter(d => d.key !== key),
      };
    }

    case OVERLAY_ACTIONS.CLOSE_ALL_DRAWERS:
      return { ...state, drawers: [] };

    // ── Global ──────────────────────────────────────────────────────
    case OVERLAY_ACTIONS.CLOSE_ALL:
      return { ...initialOverlayState };

    default:
      return state;
  }
}

